// Shared Message Centre logic: audience groups, templates, selection state,
// serialised draft saving and send-request construction. Kept free of React
// so it can be unit tested directly.

export type MessageType = "first_quote" | "feature_update";
export type SectionType = "text" | "image";

export interface MessageSection {
  type: SectionType;
  content: string;
}

export interface MessageRow {
  id: string;
  title: string;
  body: string;
  screenshotUrl: string | null;
  sections: MessageSection[] | null;
  status: "draft" | "sent";
  autoGenerated: boolean;
  sourceCommitSha: string | null;
  messageType: MessageType;
  isTemplate: boolean;
  templateName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecipientUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string | null;
  companyName: string | null;
  subscriptionStatus: string | null;
  emailUpdatesOptOut: boolean;
  firstQuoteNudgeEmailSentAt: string | null;
  firstQuoteNudgeEmailOpenedAt: string | null;
  firstQuoteNudgeEmailClickedAt: string | null;
  lastMessageSentAt: string | null;
  lastMessageOpenedAt: string | null;
}

export interface RecipientsResponse {
  users: RecipientUser[];
  total: number;
}

export type SendStatus = "accepted" | "sending" | "failed" | "uncertain";

export interface SendHistoryRow {
  id: string;
  featureUpdateId: string;
  userId: string;
  recipientEmail: string;
  sentAt: string | null;
  openedAt: string | null;
  emailMessageId: string | null;
  firstName: string | null;
  lastName: string | null;
  status: SendStatus;
  error: string | null;
}

export interface SendResult {
  sent: number;
  skipped: number;
  failed: number;
  uncertain: number;
}

// ── Groups ───────────────────────────────────────────────────────────────────

export interface MessageGroup {
  type: MessageType;
  label: string;
  shortLabel: string;
  audience: string;
  aiControls: boolean;
  autoDraft: boolean;
  legacyHistory: boolean;
}

// New audiences are added here; the composer, templates, sender and history
// all read from this definition rather than branching per group.
export const MESSAGE_GROUPS: Record<MessageType, MessageGroup> = {
  first_quote: {
    type: "first_quote",
    label: "First Quote Messages",
    shortLabel: "First quote",
    audience: "Registered builders who have never successfully generated or created a quotation.",
    aiControls: false,
    autoDraft: false,
    legacyHistory: true,
  },
  feature_update: {
    type: "feature_update",
    label: "Feature Updates",
    shortLabel: "Feature update",
    audience: "Registered builders on The Proper Tool. Opted-out users remain visible but cannot be selected.",
    aiControls: true,
    autoDraft: true,
    legacyHistory: false,
  },
};

export const MESSAGE_GROUP_ORDER: MessageType[] = ["first_quote", "feature_update"];

export function parseMessageType(value: string | null | undefined, fallback: MessageType = "feature_update"): MessageType {
  return value === "first_quote" || value === "feature_update" ? value : fallback;
}

export function normaliseMessage(row: Partial<MessageRow> & { id: string }): MessageRow {
  return {
    title: "",
    body: "",
    screenshotUrl: null,
    sections: null,
    status: "draft",
    autoGenerated: false,
    sourceCommitSha: null,
    createdAt: "",
    updatedAt: "",
    ...row,
    messageType: parseMessageType(row.messageType),
    isTemplate: row.isTemplate === true,
    templateName: row.templateName ?? null,
  };
}

export type MessageListKind = "drafts" | "sent" | "templates";

export function filterMessages(rows: MessageRow[], type: MessageType, kind: MessageListKind): MessageRow[] {
  return rows.filter(row => {
    if (row.messageType !== type) return false;
    if (kind === "templates") return row.isTemplate;
    if (row.isTemplate) return false;
    return kind === "sent" ? row.status === "sent" : row.status === "draft";
  });
}

export function isEditable(row: MessageRow): boolean {
  return row.isTemplate || row.status === "draft";
}

/** Sections from a row, falling back to legacy body/screenshotUrl. */
export function rowSections(row: Pick<MessageRow, "sections" | "body" | "screenshotUrl">): MessageSection[] {
  if (row.sections && row.sections.length > 0) return row.sections.map(s => ({ type: s.type, content: s.content }));
  const legacy: MessageSection[] = [{ type: "text", content: row.body ?? "" }];
  if (row.screenshotUrl) legacy.push({ type: "image", content: row.screenshotUrl });
  return legacy;
}

/** Strip editor ids and empty image placeholders before persisting. */
export function cleanSections(sections: { type: SectionType; content: string }[]): MessageSection[] {
  return sections
    .filter(s => !(s.type === "image" && !s.content.trim()))
    .map(({ type, content }) => ({ type, content }));
}

// ── Templates ────────────────────────────────────────────────────────────────

export interface CreateMessagePayload {
  title: string;
  sections: MessageSection[];
  messageType: MessageType;
  isTemplate?: boolean;
  templateName?: string | null;
}

/** Payload for a new draft built from a saved template or sent row. */
export function draftFromSource(
  source: { title: string; sections: MessageSection[] },
  type: MessageType,
): CreateMessagePayload {
  return { title: source.title, sections: cleanSections(source.sections), messageType: type, isTemplate: false };
}

/** Payload for saving current content as a reusable template. */
export function templateFromContent(
  content: { title: string; sections: MessageSection[] },
  type: MessageType,
  name: string,
): CreateMessagePayload {
  const templateName = name.trim();
  if (!templateName) throw new Error("Template name is required");
  return { title: content.title.trim(), sections: cleanSections(content.sections), messageType: type, isTemplate: true, templateName };
}

// ── Selection state (group-scoped) ───────────────────────────────────────────

export interface CentreState {
  group: MessageType;
  editingId: string | null;
  selection: Record<string, RecipientUser>;
}

export type CentreAction =
  | { type: "switchGroup"; group: MessageType }
  | { type: "open"; id: string | null }
  | { type: "toggle"; user: RecipientUser; group: MessageType }
  | { type: "selectMany"; users: RecipientUser[]; group: MessageType }
  | { type: "deselectMany"; ids: string[] }
  | { type: "prune"; ids: string[] }
  | { type: "clearSelection" };

export function isSelectable(user: RecipientUser): boolean {
  return !user.emailUpdatesOptOut && !!user.email;
}

export function initialCentreState(group: MessageType, preselect?: RecipientUser[]): CentreState {
  const selection: Record<string, RecipientUser> = {};
  (preselect ?? []).filter(isSelectable).forEach(u => { selection[u.id] = u; });
  return { group, editingId: null, selection };
}

export function centreReducer(state: CentreState, action: CentreAction): CentreState {
  switch (action.type) {
    case "switchGroup":
      if (action.group === state.group) return state;
      return { group: action.group, editingId: null, selection: {} };
    case "open":
      return { ...state, editingId: action.id };
    case "toggle": {
      // Actions dispatched for a group that is no longer active are ignored so
      // late events cannot leak selections across audiences.
      if (action.group !== state.group) return state;
      const next = { ...state.selection };
      if (next[action.user.id]) delete next[action.user.id];
      else if (isSelectable(action.user)) next[action.user.id] = action.user;
      return { ...state, selection: next };
    }
    case "selectMany": {
      if (action.group !== state.group) return state;
      const next = { ...state.selection };
      action.users.filter(isSelectable).forEach(u => { next[u.id] = u; });
      return { ...state, selection: next };
    }
    case "deselectMany":
    case "prune": {
      if (!action.ids.some(id => state.selection[id])) return state;
      const next = { ...state.selection };
      action.ids.forEach(id => { delete next[id]; });
      return { ...state, selection: next };
    }
    case "clearSelection":
      return Object.keys(state.selection).length === 0 ? state : { ...state, selection: {} };
  }
}

// ── Serialised save queue ────────────────────────────────────────────────────

export type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

export interface SaveQueue<T> {
  /** Mark content changed and schedule a debounced save. */
  touch: (delayMs?: number) => void;
  /** Save immediately (if dirty) after any in-flight save; resolves with the latest saved row. */
  flush: () => Promise<T>;
  /** Force a save even if not dirty. */
  saveNow: () => Promise<T>;
  /** Stop callbacks; still persists outstanding edits to the original row. */
  dispose: () => void;
  isDirty: () => boolean;
  latest: () => T;
}

export function createSaveQueue<T>(opts: {
  initial: T;
  save: () => Promise<T>;
  onSaved?: (row: T) => void;
  onError?: (err: unknown) => void;
  onState?: (state: SaveState) => void;
  defaultDelayMs?: number;
}): SaveQueue<T> {
  let chain: Promise<unknown> = Promise.resolve();
  let dirty = false;
  let disposed = false;
  let latest = opts.initial;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const emit = (s: SaveState) => { if (!disposed) opts.onState?.(s); };

  const clearTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };

  const run = (): Promise<T> => {
    clearTimer();
    const p = chain.then(async () => {
      if (!dirty) return latest;
      dirty = false;
      emit("saving");
      try {
        // save() reads the freshest content at execution time.
        const row = await opts.save();
        latest = row;
        if (!disposed) opts.onSaved?.(row);
        emit(dirty ? "pending" : "saved");
        return row;
      } catch (err) {
        dirty = true;
        if (!disposed) opts.onError?.(err);
        emit("error");
        throw err;
      }
    });
    chain = p.catch(() => undefined);
    return p;
  };

  return {
    touch: (delayMs) => {
      dirty = true;
      emit("pending");
      clearTimer();
      timer = setTimeout(() => { run().catch(() => undefined); }, delayMs ?? opts.defaultDelayMs ?? 1200);
    },
    flush: run,
    saveNow: () => { dirty = true; return run(); },
    dispose: () => {
      const wasDirty = dirty;
      disposed = true;
      clearTimer();
      if (wasDirty) run().catch(() => undefined);
    },
    isDirty: () => dirty,
    latest: () => latest,
  };
}

// ── Send ─────────────────────────────────────────────────────────────────────

export interface SendRequest {
  userIds: string[];
  confirmed: true;
  expectedUpdatedAt: string;
  messageType: MessageType;
}

/** Build the send body from the freshly saved row; refuses cross-group sends. */
export function buildSendRequest(row: MessageRow, activeGroup: MessageType, userIds: string[]): SendRequest {
  if (row.messageType !== activeGroup) throw new Error("This draft belongs to a different message group");
  if (row.isTemplate) throw new Error("Templates cannot be sent directly");
  if (!row.title.trim()) throw new Error("Add a subject before sending");
  if (!rowSections(row).some(s => s.content.trim())) throw new Error("Add content before sending");
  if (userIds.length === 0) throw new Error("Select at least one recipient");
  return { userIds: Array.from(new Set(userIds)), confirmed: true, expectedUpdatedAt: row.updatedAt, messageType: row.messageType };
}

export function describeSendResult(r: SendResult): string {
  const parts = [`${r.sent} accepted by email server`];
  if (r.skipped) parts.push(`${r.skipped} skipped`);
  if (r.failed) parts.push(`${r.failed} failed`);
  if (r.uncertain) parts.push(`${r.uncertain} uncertain (not retried)`);
  return parts.join(", ");
}

export const SEND_STATUS_LABEL: Record<SendStatus, string> = {
  accepted: "Accepted by email server",
  sending: "Sending",
  failed: "Failed",
  uncertain: "Uncertain",
};

/** Users who must not be re-selected for this message (already contacted or unknown). */
export function blockedRecipientIds(history: SendHistoryRow[]): Set<string> {
  return new Set(history.filter(h => h.status !== "failed").map(h => h.userId));
}

/** Unsettled deliveries lock content, not sending unchanged content to other recipients. */
export function hasUnsettledSends(history: SendHistoryRow[]): boolean {
  return history.some(h => h.status === "sending" || h.status === "uncertain");
}

/** Content becomes read-only after a send; the unchanged message can still reach new recipients. */
export function canSendMessage(row: MessageRow, sending: boolean, recipients: number): boolean {
  return !row.isTemplate && !sending && recipients > 0
    && !!row.title.trim() && rowSections(row).some(s => !!s.content.trim());
}

export function historyTotals(history: SendHistoryRow[]) {
  const sent = history.filter(h => (h.status ?? "accepted") === "accepted");
  const accepted = sent.length;
  const opened = sent.filter(h => h.openedAt).length;
  return {
    accepted,
    opened,
    openRate: accepted ? Math.round(opened / accepted * 100) : 0,
    failed: history.filter(h => h.status === "failed").length,
    uncertain: history.filter(h => h.status === "uncertain" || h.status === "sending").length,
  };
}

export function displayName(u: { firstName: string | null; lastName: string | null }): string {
  return [u.firstName, u.lastName].filter(Boolean).join(" ");
}
